import React, { useState } from 'react';
import {
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Typography
} from '@mui/material';
import { Translate, Check, Error } from '@mui/icons-material';
import { translationAPI } from '../services/api';

interface TranslationInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  entityType: string;
  entityId: string;
  fieldName: string;
  targetLanguage?: string;
  fullWidth?: boolean;
  required?: boolean;
  disabled?: boolean;
  multiline?: boolean;
  rows?: number;
}

const TranslationInput: React.FC<TranslationInputProps> = ({
  label,
  value,
  onChange,
  entityType,
  entityId,
  fieldName,
  targetLanguage = 'HI',
  fullWidth = true,
  required = false,
  disabled = false,
  multiline = false,
  rows = 1
}) => {
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationSuccess, setTranslationSuccess] = useState(false);
  const [translationError, setTranslationError] = useState<string | null>(null);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [currentTargetLanguage, setCurrentTargetLanguage] = useState(targetLanguage);

  const handleTranslate = async () => {
    if (!value.trim()) {
      setTranslationError('Please enter text to translate');
      setShowErrorSnackbar(true);
      return;
    }

    setIsTranslating(true);
    setTranslationError(null);
    setTranslationSuccess(false);

    try {
      const response = await translationAPI.translateAndSave({
        entityType,
        entityId,
        fieldName,
        sourceText: value,
        targetLanguage: currentTargetLanguage
      });

      if (currentTargetLanguage === 'ALL') {
        // For ALL translation, we get a map of translations but don't update the input
        setTranslationSuccess(true);
        setShowSuccessSnackbar(true);
      } else {
        // For single language translation, update the input
        const translatedText = response.data.translatedText;
        onChange(translatedText);
        setTranslationSuccess(true);
        setShowSuccessSnackbar(true);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Translation failed';
      setTranslationError(errorMessage);
      setShowErrorSnackbar(true);
    } finally {
      setIsTranslating(false);
    }
  };

  return (
    <>
      <TextField
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        fullWidth={fullWidth}
        required={required}
        disabled={disabled}
        multiline={multiline}
        rows={rows}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Box display="flex" gap={0.5}>
                <Tooltip title={`Translate to ${currentTargetLanguage}`}>
                  <IconButton
                    onClick={handleTranslate}
                    disabled={isTranslating || disabled || !value.trim()}
                    color={translationSuccess ? "success" : translationError ? "error" : "primary"}
                  >
                    {isTranslating ? (
                      <CircularProgress size={20} />
                    ) : translationSuccess ? (
                      <Check />
                    ) : translationError ? (
                      <Error />
                    ) : (
                      <Translate />
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Translate to All Languages">
                  <IconButton
                    onClick={() => {
                      const originalTargetLanguage = currentTargetLanguage;
                      setCurrentTargetLanguage('ALL');
                      handleTranslate().finally(() => {
                        setCurrentTargetLanguage(originalTargetLanguage);
                      });
                    }}
                    disabled={isTranslating || disabled || !value.trim()}
                    color="secondary"
                  >
                    <Translate />
                  </IconButton>
                </Tooltip>
              </Box>
            </InputAdornment>
          ),
        }}
      />
      
      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSuccessSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowSuccessSnackbar(false)} severity="success">
          {currentTargetLanguage === 'ALL' ? 'Text translated to all languages successfully!' : 'Text translated successfully!'}
        </Alert>
      </Snackbar>

      <Snackbar
        open={showErrorSnackbar}
        autoHideDuration={4000}
        onClose={() => setShowErrorSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowErrorSnackbar(false)} severity="error">
          {translationError}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TranslationInput; 